import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { HexColorPicker } from 'react-colorful';
import {
  BANNER_SNAP_VIDEO_BOTTOM_PCT,
  type AudioSource,
  type BannerPosition,
} from '@content-engine/shared';
import { ApiError } from '../../lib/api.ts';
import { BannerPlacementEditor } from '../../components/BannerPlacementEditor.tsx';
import {
  createVariantApi,
  fetchBanner,
  fetchBanners,
  fetchClip,
  type CreateVariantParams,
} from '../../lib/queries.ts';
import type { Banner, ClipBackgroundMode, SubtitlePosition } from '../../lib/types.ts';

export function BannerModal({
  clipId,
  onClose,
  onApplied,
}: {
  clipId: string;
  onClose: () => void;
  onApplied: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const [bannerId, setBannerId] = useState<string>('');
  const [tagFilter, setTagFilter] = useState('');
  const [position, setPosition] = useState<BannerPosition>('bottom-right');
  const [sizePct, setSizePct] = useState(20);
  const [useTimeWindow, setUseTimeWindow] = useState(false);
  const [startSec, setStartSec] = useState(0);
  const [endSec, setEndSec] = useState(0);
  const [loop, setLoop] = useState(true);
  // Freeze-frame mode: the clip pauses at freezeAtSec for freezeDurationSec
  // while the banner plays over the frozen frame (with its audio).
  const [timingMode, setTimingMode] = useState<'overlay' | 'freeze'>('overlay');
  const [freezeAtSec, setFreezeAtSec] = useState(1);
  const [freezeDurationSec, setFreezeDurationSec] = useState(3);
  const [useChroma, setUseChroma] = useState(true);
  const [previewChroma, setPreviewChroma] = useState(false);
  const [chromaColor, setChromaColor] = useState('0x00B140');
  const [showPicker, setShowPicker] = useState(false);
  const [pickingColor, setPickingColor] = useState(false);
  // null = no hint shown; 'not-ready' = media still loading (retry likely to
  // work); 'tainted' = CORS/tainted-canvas or fully-transparent sample (won't
  // resolve by retrying).
  const [pickUnavailableReason, setPickUnavailableReason] = useState<'tainted' | 'not-ready' | null>(null);
  const [chromaSimilarity, setChromaSimilarity] = useState(0.15);
  const [chromaBlend, setChromaBlend] = useState(0.05);
  const [cropTop, setCropTop] = useState(0);
  const [cropRight, setCropRight] = useState(0);
  const [cropBottom, setCropBottom] = useState(0);
  const [cropLeft, setCropLeft] = useState(0);
  const [audioSource, setAudioSource] = useState<AudioSource>('clip');
  const [positionXPct, setPositionXPct] = useState<number>(50);
  const [positionYPct, setPositionYPct] = useState<number>(BANNER_SNAP_VIDEO_BOTTOM_PCT);
  const [backgroundMode, setBackgroundMode] = useState<ClipBackgroundMode>('blur');
  const [backgroundBannerId, setBackgroundBannerId] = useState<string | null>(null);
  const [subtitlePosition, setSubtitlePosition] = useState<SubtitlePosition>('bottom');
  const [burnSubtitles, setBurnSubtitles] = useState(true);

  // Background-video pool (purpose='background', kind='video'). Loaded lazily
  // when the user picks bgMode='video'.
  const bgBannersQuery = useQuery({
    queryKey: ['banners', 'background'],
    queryFn: () => fetchBanners({ status: 'active', purpose: 'background' }),
    enabled: backgroundMode === 'video',
    staleTime: 5 * 60 * 1000,
  });
  const bgBanners = (bgBannersQuery.data?.banners ?? []).filter((b) => b.kind === 'video');

  // Clip foundation presigned URL — the new placement editor needs it as
  // the canvas background (first frame). One fetch per modal open.
  const clipQuery = useQuery({
    queryKey: ['clip', clipId],
    queryFn: () => fetchClip(clipId),
    enabled: !!clipId,
  });
  const clipPresignedUrl = clipQuery.data?.presignedUrl;

  // Overlay-banner pool: only purpose='banner' (small overlays on top of video).
  // Background-purpose banners go through the separate selector below.
  const bannersQuery = useQuery({
    queryKey: ['banners', 'active', 'overlay', tagFilter],
    queryFn: () => fetchBanners({ status: 'active', purpose: 'banner', ...(tagFilter ? { tag: tagFilter } : {}) }),
  });
  const banners = bannersQuery.data?.banners ?? [];
  const selected: Banner | undefined = banners.find((b) => b.id === bannerId);

  // Pre-signed media URL for the selected banner — needed for visual crop.
  const bannerDetailQuery = useQuery({
    queryKey: ['banner', selected?.id],
    queryFn: () => fetchBanner(selected!.id),
    enabled: !!selected,
  });
  const selectedMediaUrl = bannerDetailQuery.data?.presignedUrl;

  // When user picks a banner, pre-fill the form with its defaults.
  useEffect(() => {
    if (!selected) return;
    setPosition(selected.defaultPosition);
    setSizePct(selected.defaultSizePct);
    setLoop(selected.defaultLoop);
    if (selected.defaultChromaColor) {
      setUseChroma(true);
      setChromaColor(selected.defaultChromaColor);
      setChromaSimilarity(Number(selected.defaultChromaSimilarity ?? '0.15'));
      setChromaBlend(Number(selected.defaultChromaBlend ?? '0.05'));
    } else {
      setUseChroma(false);
    }
    setCropTop(Number(selected.defaultCropTopPct ?? '0'));
    setCropRight(Number(selected.defaultCropRightPct ?? '0'));
    setCropBottom(Number(selected.defaultCropBottomPct ?? '0'));
    setCropLeft(Number(selected.defaultCropLeftPct ?? '0'));
    setAudioSource(selected.defaultAudioSource ?? 'clip');
    setPickingColor(false);
    setPickUnavailableReason(null);
    // Seed coords from enum preset so the canvas starts at a sensible spot.
    // sizePct was just reset to selected.defaultSizePct on the same tick,
    // so we pass the fresh value (the local sizePct state is stale here).
    const seed = presetToCoords(selected.defaultPosition, selected.defaultSizePct);
    setPositionXPct(seed.x);
    setPositionYPct(seed.y);
  }, [selected]);

  const isVideo = selected?.kind === 'video';

  const applyMutation = useMutation({
    mutationFn: () => {
      const hasOverlay = !!bannerId;
      const params: CreateVariantParams = {
        ...(hasOverlay ? { bannerLibraryId: bannerId } : {}),
        ...(hasOverlay
          ? {
              position,
              sizePct,
              ...(timingMode === 'freeze'
                ? { bannerTimingMode: 'freeze' as const, freezeAtSec, freezeDurationSec }
                : {
                    ...(useTimeWindow ? { startSec, endSec } : {}),
                    ...(isVideo ? { loop } : {}),
                  }),
              ...(isVideo && useChroma
                ? { chromaKeyColor: chromaColor, chromaSimilarity, chromaBlend }
                : isVideo
                  ? { disableChromaKey: true }
                  : {}),
              cropTopPct: cropTop,
              cropRightPct: cropRight,
              cropBottomPct: cropBottom,
              cropLeftPct: cropLeft,
              positionXPct,
              positionYPct,
            }
          : {}),
        audioSource,
        backgroundMode,
        ...(backgroundMode === 'video' && backgroundBannerId ? { backgroundBannerId } : {}),
        subtitlePosition,
        burnSubtitles,
      };
      return createVariantApi(clipId, params);
    },
    onSuccess: () => onApplied(),
  });

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    applyMutation.mutate();
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 200,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 1200,
          maxWidth: '95vw',
          background: 'var(--bg-card)',
          border: '1px solid var(--line)',
          borderRadius: 12,
          padding: 24,
          maxHeight: '95vh',
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: 18, marginBottom: 16 }}>{t('clips.bannerModal.title')}</h2>
        <form onSubmit={submit}>
          <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 20, alignItems: 'start' }}>
            {/* LEFT COLUMN — controls */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* 1. Background mode — split into two clear groups */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 600 }}>
                  1. {t('variants.bgMode.label')}
                </span>

                {/* Group A: clip visible */}
                <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('variants.bgMode.groupClipVisible')}
                </div>
                {(['blur', 'letterbox'] as ClipBackgroundMode[]).map((m) => (
                  <label key={m} style={modalCheckboxLabelStyle}>
                    <input
                      type="radio"
                      name="bgmode"
                      value={m}
                      checked={backgroundMode === m}
                      onChange={() => setBackgroundMode(m)}
                      style={modalCheckboxInputStyle}
                    />
                    {t(`variants.bgMode.${m}`)}
                  </label>
                ))}

                {/* Group B: clip hidden — audio + subs only */}
                <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('variants.bgMode.groupClipHidden')}
                </div>
                {(['video', 'black'] as ClipBackgroundMode[]).map((m) => (
                  <label key={m} style={modalCheckboxLabelStyle}>
                    <input
                      type="radio"
                      name="bgmode"
                      value={m}
                      checked={backgroundMode === m}
                      onChange={() => setBackgroundMode(m)}
                      style={modalCheckboxInputStyle}
                    />
                    {t(`variants.bgMode.${m}`)}
                  </label>
                ))}
                {backgroundMode === 'video' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                    <label style={{ minWidth: 60, margin: 0, fontSize: 12, color: 'var(--text-3)' }}>
                      {t('variants.bgMode.videoPick')}
                    </label>
                    <select
                      value={backgroundBannerId ?? ''}
                      onChange={(e) => setBackgroundBannerId(e.target.value || null)}
                      style={{ flex: 1 }}
                    >
                      <option value="">— {t('variants.bgMode.videoPickPlaceholder')} —</option>
                      {bgBanners.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* 2. Overlay banner (optional) + its settings */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
                <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 600 }}>
                  2. {t('clips.bannerModal.overlaySectionLabel')}
                </span>
                <div className="form-row">
                  <label>{t('clips.bannerModal.tagFilterLabel')}</label>
                  <input
                    type="text"
                    value={tagFilter}
                    onChange={(e) => setTagFilter(e.target.value)}
                    placeholder={t('clips.bannerModal.tagFilterPlaceholder')}
                  />
                </div>
                <div className="form-row">
                  <label>{t('clips.bannerModal.pickBannerLabel')}</label>
                  <select value={bannerId} onChange={(e) => setBannerId(e.target.value)}>
                    <option value="">— {t('clips.bannerModal.noOverlayOption')} —</option>
                    {banners.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name} ({b.kind}, {b.tags.join(', ') || '—'})
                      </option>
                    ))}
                  </select>
                </div>
                {selected && (
                  <>
                    <div className="form-row">
                      <label>{t('clips.bannerModal.timingModeLabel')}</label>
                      <select value={timingMode} onChange={(e) => setTimingMode(e.target.value as 'overlay' | 'freeze')}>
                        <option value="overlay">{t('clips.bannerModal.timingMode_overlay')}</option>
                        <option value="freeze">{t('clips.bannerModal.timingMode_freeze')}</option>
                      </select>
                    </div>
                    {timingMode === 'freeze' && (
                      <div className="form-row inline">
                        <div>
                          <label>{t('clips.bannerModal.freezeAt')}</label>
                          <input type="number" min={0.1} step={0.1} value={freezeAtSec} onChange={(e) => setFreezeAtSec(Number(e.target.value))} />
                        </div>
                        <div>
                          <label>{t('clips.bannerModal.freezeDuration')}</label>
                          <input type="number" min={0.5} max={60} step={0.5} value={freezeDurationSec} onChange={(e) => setFreezeDurationSec(Number(e.target.value))} />
                        </div>
                      </div>
                    )}
                    {timingMode === 'overlay' && (
                      <>
                        <label style={modalCheckboxLabelStyle}>
                          <input type="checkbox" checked={useTimeWindow} onChange={(e) => setUseTimeWindow(e.target.checked)} style={modalCheckboxInputStyle} />
                          <span>{t('clips.bannerModal.timeWindowLabel')}</span>
                        </label>
                        {useTimeWindow && (
                          <div className="form-row inline">
                            <div>
                              <label>{t('clips.bannerModal.fromSec')}</label>
                              <input type="number" min={0} step={0.5} value={startSec} onChange={(e) => setStartSec(Number(e.target.value))} />
                            </div>
                            <div>
                              <label>{t('clips.bannerModal.toSec')}</label>
                              <input type="number" min={0} step={0.5} value={endSec} onChange={(e) => setEndSec(Number(e.target.value))} />
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {isVideo && (
                      <>
                        {timingMode === 'overlay' && (
                          <label style={modalCheckboxLabelStyle}>
                            <input type="checkbox" checked={loop} onChange={(e) => setLoop(e.target.checked)} style={modalCheckboxInputStyle} />
                            <span>{t('clips.bannerModal.loopLabel')}</span>
                          </label>
                        )}
                        <label style={modalCheckboxLabelStyle}>
                          <input type="checkbox" checked={useChroma} onChange={(e) => setUseChroma(e.target.checked)} style={modalCheckboxInputStyle} />
                          <span>{t('clips.bannerModal.chromaToggleLabel')}</span>
                        </label>
                        {useChroma && (
                          /* Chroma controls always visible now (no Advanced toggle).
                             Color: HTML5 picker (visual) + hex text input (precision),
                             synced both ways. Accepts both #RRGGBB and 0xRRGGBB. */
                          <div style={{ padding: 10, background: 'var(--bg-elev)', borderRadius: 6, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div className="form-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <label style={{ minWidth: 80, margin: 0, fontSize: 12 }}>
                                {t('clips.bannerModal.chromaColorLabel')}
                              </label>
                              {/* Swatch toggles an IN-PAGE visual picker
                                  (react-colorful) — full gradient + hue slider,
                                  any colour, rendered inside the page. We do NOT
                                  use the native <input type="color">: its Windows
                                  "pick from screen" eyedropper wedges input
                                  (especially over RDP) and can't be dismissed.
                                  Presets = quick shortcuts, hex field = precise. */}
                              <button
                                type="button"
                                aria-label={t('clips.bannerModal.chromaColorLabel')}
                                title={chromaColor}
                                onClick={() => setShowPicker((v) => !v)}
                                style={{
                                  width: 28, height: 28, borderRadius: 4, flexShrink: 0, padding: 0, cursor: 'pointer',
                                  border: showPicker ? '2px solid var(--neon)' : '1px solid var(--line)',
                                  background: chromaHexToColorInput(chromaColor),
                                }}
                              />
                              {/* In-app eyedropper — samples the actual banner pixel under
                                  the click instead of guessing a shade in the gradient
                                  picker. Replaces the native <input type="color"> "pick
                                  from screen" tool, which wedges input over RDP. */}
                              <button
                                type="button"
                                aria-label={t('clips.bannerModal.pickFromBanner')}
                                title={t('clips.bannerModal.pickFromBanner')}
                                disabled={!selected || !selectedMediaUrl}
                                onClick={() => {
                                  setPickUnavailableReason(null);
                                  setPickingColor((v) => !v);
                                }}
                                style={{
                                  width: 28, height: 28, borderRadius: 4, flexShrink: 0, padding: 0,
                                  cursor: !selected || !selectedMediaUrl ? 'not-allowed' : 'pointer',
                                  border: pickingColor ? '2px solid var(--neon)' : '1px solid var(--line)',
                                  background: pickingColor ? 'var(--neon)' : 'var(--bg-elev)',
                                  fontSize: 14,
                                  opacity: !selected || !selectedMediaUrl ? 0.5 : 1,
                                }}
                              >
                                💧
                              </button>
                              {CHROMA_PRESETS.map((preset) => (
                                <button
                                  key={preset}
                                  type="button"
                                  title={preset}
                                  onClick={() => setChromaColor(preset)}
                                  style={{
                                    width: 22, height: 22, borderRadius: 4, padding: 0,
                                    cursor: 'pointer', flexShrink: 0,
                                    background: chromaHexToColorInput(preset),
                                    border: chromaColor.toUpperCase() === preset.toUpperCase()
                                      ? '2px solid var(--neon)'
                                      : '1px solid var(--line)',
                                  }}
                                />
                              ))}
                              <input
                                type="text"
                                value={chromaColor}
                                onChange={(e) => setChromaColor(e.target.value)}
                                style={{ fontFamily: 'var(--mono)', fontSize: 12, flex: 1 }}
                                placeholder="0x00B140"
                              />
                            </div>
                            {showPicker && (
                              <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 4 }}>
                                <HexColorPicker
                                  color={chromaHexToColorInput(chromaColor)}
                                  onChange={(hex) => setChromaColor(`0x${hex.replace('#', '').toUpperCase()}`)}
                                  style={{ width: 200, height: 160 }}
                                />
                              </div>
                            )}
                            {pickingColor && (
                              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                                {t('clips.bannerModal.pickFromBanner')}…
                              </div>
                            )}
                            {pickUnavailableReason && (
                              <div style={{ fontSize: 11, color: 'var(--hot)' }}>
                                {t(
                                  pickUnavailableReason === 'not-ready'
                                    ? 'clips.bannerModal.pickFromBannerNotReady'
                                    : 'clips.bannerModal.pickFromBannerUnavailable',
                                )}
                              </div>
                            )}
                            <div className="form-row">
                              <label>{t('clips.bannerModal.chromaSimilarityLabel')}: {chromaSimilarity.toFixed(2)}</label>
                              <input type="range" min={0.01} max={0.5} step={0.01} value={chromaSimilarity} onChange={(e) => setChromaSimilarity(Number(e.target.value))} />
                            </div>
                            <div className="form-row">
                              <label>{t('clips.bannerModal.chromaBlendLabel')}: {chromaBlend.toFixed(2)}</label>
                              <input type="range" min={0} max={0.3} step={0.01} value={chromaBlend} onChange={(e) => setChromaBlend(Number(e.target.value))} />
                            </div>
                            <label style={{ ...modalCheckboxLabelStyle, marginTop: 4 }}>
                              <input
                                type="checkbox"
                                checked={previewChroma}
                                onChange={(e) => setPreviewChroma(e.target.checked)}
                                style={modalCheckboxInputStyle}
                              />
                              <span>{t('clips.bannerModal.previewChromaLabel')}</span>
                            </label>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>

              {/* 3. Audio source */}
              <div className="form-row" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
                <label style={{ fontWeight: 600 }}>3. {t('clips.bannerModal.audioSourceLabel')}</label>
                <select value={audioSource} onChange={(e) => setAudioSource(e.target.value as AudioSource)}>
                  <option value="clip">{t('banners.uploadModal.audioSource_clip')}</option>
                  <option value="banner" disabled={!selected || !isVideo}>
                    {t('banners.uploadModal.audioSource_banner')}
                    {(!selected || !isVideo) && ' — ' + t('clips.bannerModal.audioOverlayHint')}
                  </option>
                  <option value="background" disabled={backgroundMode !== 'video' || !backgroundBannerId}>
                    {t('banners.uploadModal.audioSource_background')}
                    {(backgroundMode !== 'video' || !backgroundBannerId) && ' — ' + t('clips.bannerModal.audioBgHint')}
                  </option>
                  <option value="mute">{t('banners.uploadModal.audioSource_mute')}</option>
                </select>
              </div>

              {/* 4. Subtitles */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
                <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 600 }}>4. {t('variants.subs.label')}</span>
                <label style={modalCheckboxLabelStyle}>
                  <input type="checkbox" checked={burnSubtitles} onChange={(e) => setBurnSubtitles(e.target.checked)} style={modalCheckboxInputStyle} />
                  {t('variants.subs.burn')}
                </label>
                <div className="form-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ minWidth: 60, margin: 0, fontSize: 12, color: 'var(--text-3)' }}>
                    {t('variants.subs.position')}
                  </label>
                  <select
                    value={subtitlePosition}
                    onChange={(e) => setSubtitlePosition(e.target.value as SubtitlePosition)}
                    disabled={!burnSubtitles}
                    style={{ flex: 1 }}
                  >
                    <option value="top">{t('variants.subs.top')}</option>
                    <option value="middle">{t('variants.subs.middle')}</option>
                    <option value="bottom">{t('variants.subs.bottom')}</option>
                  </select>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN — WYSIWYG canvas (when overlay) or clip preview */}
            <div>
              {selected && selectedMediaUrl && clipPresignedUrl ? (
                <BannerPlacementEditor
                  clipPresignedUrl={clipPresignedUrl}
                  banner={{ mediaUrl: selectedMediaUrl, kind: selected.kind }}
                  chromaPreview={
                    previewChroma && useChroma
                      ? { color: chromaColor, similarity: chromaSimilarity, blend: chromaBlend }
                      : null
                  }
                  pickingColor={pickingColor}
                  onPickColor={(hex) => {
                    setChromaColor(`0x${hex}`);
                    setPickingColor(false);
                  }}
                  onPickUnavailable={(reason) => {
                    setPickUnavailableReason(reason ?? 'tainted');
                    setPickingColor(false);
                  }}
                  value={{
                    positionXPct,
                    positionYPct,
                    sizePct,
                    crop: { top: cropTop, right: cropRight, bottom: cropBottom, left: cropLeft },
                  }}
                  onChange={(next) => {
                    setPositionXPct(next.positionXPct);
                    setPositionYPct(next.positionYPct);
                    setSizePct(next.sizePct);
                    setCropTop(next.crop.top);
                    setCropRight(next.crop.right);
                    setCropBottom(next.crop.bottom);
                    setCropLeft(next.crop.left);
                  }}
                />
              ) : clipPresignedUrl ? (
                <div style={{ aspectRatio: '9 / 16', maxHeight: '70vh', margin: '0 auto', background: '#000', borderRadius: 6, overflow: 'hidden' }}>
                  <video
                    src={clipPresignedUrl}
                    controls
                    muted
                    crossOrigin="anonymous"
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </div>
              ) : (
                <div style={{ aspectRatio: '9 / 16', maxHeight: '70vh', margin: '0 auto', background: 'var(--bg-elev)', border: '1px dashed var(--line)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontSize: 12, padding: 16, textAlign: 'center' }}>
                  {t('common.loading')}
                </div>
              )}
            </div>
          </div>
          {applyMutation.error && (
            <div style={{ color: 'var(--hot)', fontSize: 12, marginBottom: 12 }}>
              {applyMutation.error instanceof ApiError ? applyMutation.error.message : (applyMutation.error as Error).message}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" onClick={onClose}>{t('common.cancel')}</button>
            <button type="submit" className="primary" disabled={applyMutation.isPending}>
              {applyMutation.isPending ? t('clips.bannerModal.submitting') : t('clips.bannerModal.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Left-aligned, fixed-size checkboxes for the modal's left-column control
 *  rows. .form-row's CSS gives every <input> width:100% + padding:10px 12px,
 *  which makes naked checkboxes stretch into ugly boxes; these inline styles
 *  bypass the class entirely. */
const modalCheckboxLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 12,
  color: 'var(--text-2)',
  cursor: 'pointer',
  marginBottom: 0,
};
const modalCheckboxInputStyle: React.CSSProperties = {
  width: 16,
  height: 16,
  padding: 0,
  margin: 0,
  flexShrink: 0,
};

/** ffmpeg chroma color is conventionally written as '0xRRGGBB'; HTML5
 *  <input type="color"> wants '#RRGGBB'. These two helpers bridge the two,
 *  tolerating both forms on input so the moderator can paste either. */
function chromaHexToColorInput(value: string): string {
  const cleaned = value.trim().replace(/^0x/i, '').replace(/^#/, '');
  return /^[0-9a-f]{6}$/i.test(cleaned) ? `#${cleaned.toLowerCase()}` : '#000000';
}

/** Common chroma-key colours (ffmpeg '0xRRGGBB' form): green / blue / magenta
 *  screens. Offered as one-click presets in place of the removed native colour
 *  picker; the hex text field handles any other value. */
const CHROMA_PRESETS = ['0x00B140', '0x0047BB', '0xFF00FF'];

function presetToCoords(preset: BannerPosition, sizePct: number): { x: number; y: number } {
  // Approximate top-left percent from enum preset. Banner height depends on
  // aspect (only known after the image/video loads); we use sizePct as a
  // proxy here — user can drag from there.
  const map: Record<BannerPosition, { x: number; y: number }> = {
    'top-left':      { x: 2, y: 2 },
    'top-center':    { x: (100 - sizePct) / 2, y: 2 },
    'top-right':     { x: 100 - sizePct - 2, y: 2 },
    'bottom-left':   { x: 2, y: 100 - sizePct - 2 },
    'bottom-center': { x: (100 - sizePct) / 2, y: 100 - sizePct - 2 },
    'bottom-right':  { x: 100 - sizePct - 2, y: 100 - sizePct - 2 },
    'center':        { x: (100 - sizePct) / 2, y: (100 - sizePct) / 2 },
  };
  return map[preset];
}

